/**
 * UI 主题 registry —— 多主题引擎的核心。
 *
 * 每个主题同时声明：
 *   - id / name / description / preview：颜色与文案
 *   - skinFamily：形态家族（控件形态/氛围/动效的设计语言分组）
 *
 * 切换由前端 `<html data-theme="...">`（颜色）+ `<html data-skin="...">`（形态）双属性驱动；
 * 明暗（class="dark"）与两者正交共存。详见 apps/web/src/app/globals.css 的「形态家族皮肤」区块。
 *
 * 新增主题步骤：
 *   1. 在此数组追加一条 { id, skinFamily, name, description, builtin, preview }
 *   2. 在 globals.css 追加 `:root[data-theme="{id}"]` 及深色覆盖（颜色 token）
 *   3. 若引入全新形态家族：在 UiSkinId 联合类型登记，并在 globals.css「形态家族皮肤」
 *      追加 `:root[data-skin="{family}"]` 的氛围层/控件形态层/动效层
 *
 * name/description 内嵌双语，避免为固定术语污染全部 locale 文件。
 */
export const UI_THEMES = [
    {
        id: 'glass',
        skinFamily: 'glass',
        name: { zh: '毛玻璃', en: 'Glass' },
        description: {
            zh: '半透明模糊的现代质感（默认）',
            en: 'Translucent blurred modern look (default)',
        },
        builtin: true,
        preview: { primary: '#3b82f6', surface: 'rgba(255,255,255,0.7)', border: 'rgba(255,255,255,0.4)' },
    },
    {
        id: 'flat',
        skinFamily: 'refined',
        name: { zh: '扁平', en: 'Flat' },
        description: {
            zh: '实心纯净，清晰高效',
            en: 'Solid, clean and efficient',
        },
        builtin: true,
        preview: { primary: '#2563eb', surface: '#ffffff', border: '#e2e8f0' },
    },
    {
        id: 'professional',
        skinFamily: 'refined',
        name: { zh: '专业商务', en: 'Professional' },
        description: {
            zh: '实色克制，企业级层级阴影，高信息密度',
            en: 'Solid, restrained, enterprise elevation, dense',
        },
        builtin: true,
        preview: { primary: '#1d4ed8', surface: '#ffffff', border: '#e2e8f0' },
    },
    {
        id: 'minimal',
        skinFamily: 'refined',
        name: { zh: '极简纸感', en: 'Minimal' },
        description: {
            zh: '大量留白，极细边框，无阴影，文档阅读感',
            en: 'Generous whitespace, hairlines, shadowless',
        },
        builtin: true,
        preview: { primary: '#1c1917', surface: '#ffffff', border: '#ebe9e6' },
    },
    {
        id: 'neumorphism',
        skinFamily: 'soft',
        name: { zh: '新拟物', en: 'Neumorphism' },
        description: {
            zh: '柔和凹凸阴影，单色软触感',
            en: 'Soft concave/convex shadows, tactile',
        },
        builtin: true,
        preview: { primary: '#5a6478', surface: '#e0e5ec', border: '#d1d5db' },
    },
    {
        id: 'cyber',
        skinFamily: 'neon',
        name: { zh: '暗黑科技', en: 'Cyber' },
        description: {
            zh: '深色底，霓虹发光，未来感',
            en: 'Dark base, neon glow, futuristic',
        },
        builtin: true,
        preview: { primary: '#00e5ff', surface: '#11111d', border: '#00e5ff' },
    },
    {
        id: 'ink-stone',
        skinFamily: 'ink',
        name: { zh: '墨石', en: 'Ink-Stone' },
        description: {
            zh: '暖调象牙白 + 墨色克制 + 衬线标题，温润书卷的端砚质感',
            en: 'Warm ivory, restrained ink accent, serif headings — scholarly ink-stone',
        },
        builtin: true,
        preview: { primary: '#1F1B16', surface: '#FAF8F3', border: '#E0D9CC' },
    },
    {
        id: 'mica',
        skinFamily: 'refined',
        name: { zh: '云母', en: 'Mica' },
        description: {
            zh: '莫兰迪高级灰，雾面云母石，冷静克制',
            en: 'Morandi high-grade grey, frosted mica stone, cool restraint',
        },
        builtin: true,
        preview: { primary: '#4A6B8A', surface: '#F7F8FA', border: '#E2E5EA' },
    },
    {
        id: 'xuan',
        skinFamily: 'ink',
        name: { zh: '宣纸', en: 'Xuan' },
        description: {
            zh: '书卷留白，暖米宣纸 + 宋体标题 + 朱砂点缀，近直角无阴影',
            en: "Scholar's rice paper, Song-serif, cinnabar, hairline-layered",
        },
        builtin: true,
        preview: { primary: '#1A1814', surface: '#FBF9F2', border: '#E8E1CF' },
    },
    {
        id: 'obsidian',
        skinFamily: 'luxury',
        name: { zh: '玄夜', en: 'Obsidian' },
        description: {
            zh: '午夜鎏金，哑光黑石 + 香槟金描边，深邃低调奢华（深色为主）',
            en: 'Midnight gilded, matte obsidian + champagne gold, dark-first luxury',
        },
        builtin: true,
        preview: { primary: '#C8A96A', surface: '#121419', border: '#2A2E3A' },
    },
    {
        id: 'washi',
        skinFamily: 'wabi',
        name: { zh: '素白', en: 'Washi' },
        description: {
            zh: '侘寂留白——暖和紙底 + 枯枝褐/苔绿大地色 + humanist 暖字 + 纸张层叠柔影，温润安静的手作质感',
            en: 'Wabi-sabi negative space — warm washi paper, withered-brown & moss earth tones, humanist sans, soft paper-layered shadows',
        },
        builtin: true,
        preview: { primary: '#6b5538', surface: '#f2efe8', border: '#e0dad0' },
    },
    {
        id: 'aurora',
        skinFamily: 'liquid',
        name: { zh: '极光液璃', en: 'Aurora' },
        description: {
            zh: 'Apple Liquid Glass 忠实材质——凸面镜面边缘高光 + 通透折射感 + 浮动胶囊 chrome + 弹性动效',
            en: 'Apple Liquid Glass — convex specular rim, refractive translucency, floating capsule chrome, spring motion',
        },
        builtin: true,
        preview: { primary: '#3b82f6', surface: 'rgba(255,255,255,0.55)', border: 'rgba(255,255,255,0.18)' },
    },
];
/** 默认 UI 主题（未配置、未知值或回退时使用） */
export const DEFAULT_UI_THEME = 'glass';
/** 默认形态家族（与 DEFAULT_UI_THEME 一致：glass 主题 → glass 家族） */
export const DEFAULT_SKIN = 'glass';
/** 判断 id 是否为已注册的有效 UI 主题（类型守卫） */
export function isValidUiTheme(id) {
    return UI_THEMES.some((t) => t.id === id);
}
/** 按 id 查询主题定义；未找到返回 undefined */
export function getUiThemeById(id) {
    return UI_THEMES.find((t) => t.id === id);
}
/** 按主题 id 查询其形态家族；未找到回退 DEFAULT_SKIN */
export function getSkinForTheme(themeId) {
    return getUiThemeById(themeId)?.skinFamily ?? DEFAULT_SKIN;
}
//# sourceMappingURL=ui-theme.js.map